import React, { useState, useContext, useEffect } from "react";
import { Box, Button, Layer, Grid, Text, Tabs, Tab } from "grommet";
import { Deploy } from "grommet-icons";
import { AgGridReact } from "ag-grid-react";
import { themeBalham } from 'ag-grid-community';

import { gql, useQuery } from '@apollo/client';
import * as moment from "moment-timezone";

import  { Spinner } from "../../common/Spinner";
import UserContext from "../../../context/UserContext";

// import { useGetEnvHostService, useGetTimestamps, DEFAULT_LIMIT_INCREMENT } from "../api/ZookeeperHistoryApi";
// import { ServiceHistoryDetailAthena } from "./ServiceHistoryDetailsAthena";
import { ServiceHistoryDetailPostgres } from "./ServiceHistoryDetailsPostgres";

// Cannot use distinct_on together with order_by timestamp:desc
// so we will need to process the data in the frontend and
// use the first entry from each instance to generate the list
const GENERIC_ZK_DATA_QUERY = gql`
query ($node: String!, $env: String!, $service: String!) {
  zkdata_max_ts(
    where: {
      env: { _eq: $env }
      node: { _eq: $node }
      service: { _eq: $service }
    }
  ) {
    instance
    timestamp: max_timestamp
    process_id
  }
}
`;

const OC_ZK_DATA_QUERY = gql`
  query($node: String!, $env: String!, $service: String!, $ocServiceName: String!) {
    zkdata_max_ts(
      where: {
        node: { _eq: $node }
        env: { _eq: $env }
        service: { _eq: $service }
        service_name: { _eq: $ocServiceName }
      }
    ) {
      instance
      process_id
      timestamp: max_timestamp
    }
  }
`;

const ZK_DATA_TIMESTAMP = gql`
  query($instance: String!, $limit: Int!, $process_id: Int!) {
    zkdata(
      order_by: { timestamp: desc }
      limit: $limit
      where: { 
        instance: { _eq: $instance } 
        process_id: { _eq: $process_id }
      }
    ) {
      timestamp
    }
  }
`;

export const ServiceHistory = ({
    env,
    host,
    service,
    instance,
    setShowServiceHistory,
    mdsConsumer = false,
    ocServiceName = ""
}) => {
    const [index, setIndex] = useState(0);
    const onActive = nextIndex => setIndex(nextIndex);
    const daysAgo = (process.env.REACT_APP_ENVIRONMENT.startsWith("int") || process.env.REACT_APP_ENVIRONMENT === "local") ? "14" : "30"

    return (
        <Layer
            full="horizontal"
            position="center"
            margin={{ left: "20px", right: "20px" }}
            onEsc={() => {
                setShowServiceHistory(false);
            }}
            onClickOutside={() => {
                setShowServiceHistory(false);
            }}
        >
            <Tabs
                alignControls="start"
                onActive={onActive}
                activeIndex={index}
                margin={{ top: "5px", bottom: "10px", left: "10px", right: "20px" }}
            >
                <Tab
                    icon={<Deploy />}
                    title={
                        <Text
                            weight="bold"
                            color={index === 0 ? 'ttok' : 'dark-1'}
                            size="small" margin="xxsmall">
                            Last {daysAgo} days
                        </Text>
                    }>
                    <ServiceHistoryPostgres
                        env={env}
                        host={host}
                        service={service}
                        instance={instance}
                        mdsConsumer={mdsConsumer}
                        ocServiceName={ocServiceName}
                    />
                </Tab>
            </Tabs>
        </Layer>
    );
}

const ServiceHistoryPostgres = ({
    env,
    host,
    service,
    instance,
    mdsConsumer = false,
    ocServiceName = ""
}) => {
    if (mdsConsumer && service === 'mds') {
        service = "market_data_server";
    }

    const [clickInstance, setClickInstance] = useState("");
    const [clickPID, setClickPID] = useState(0);
    const [clickTimestamp, setClickTimestamp] = useState("");

    const [leftPanel, setLeftPanel] = useState([]);
    const [middlePanel, setMiddlePanel] = useState([]);

    const ZK_DATA_QUERY = service === "oc" ? OC_ZK_DATA_QUERY : GENERIC_ZK_DATA_QUERY
    const query_vars = service === "oc" ? { node: host, env: env, service: service, ocServiceName: ocServiceName } : { node: host, env: env, service: service }
    const resultPostgres = useQuery(ZK_DATA_QUERY, {
        variables: query_vars,
        notifyOnNetworkStatusChange: true
    });

    const DEFAULT_POSTGRES_LIMIT_INCREMENT = 5000;
    const [limit, setLimit] = useState(DEFAULT_POSTGRES_LIMIT_INCREMENT);
    const resultInstanceHistory = useQuery(ZK_DATA_TIMESTAMP, {
        variables: { instance: clickInstance, limit: limit, process_id: clickPID },
        notifyOnNetworkStatusChange: true,
    });

    const handleClickNodeHistory = (instance, process_id) => {
        setClickInstance(instance);
        setClickPID(process_id);
    };

    const handleClickTimestamp = (timestamp) => {
        setClickTimestamp(timestamp);
    };

    const handleClickRefresh = () => {
        handleClickTimestamp("");
        handleClickNodeHistory("", 0);
        resultPostgres.refetch();
        resultInstanceHistory.refetch();
    }

    const token = useContext(UserContext);

    const leftPanelColumns = [
        {
            field: "instance",
            headerName: "Instance",
            minWidth: 250,
        },
        {
            field: "timestamp",
            headerName: "timestamp",
            minWidth: 150,
            hide: true
        },
        {
            field: "process_id",
            headerName: "PID",
            minWidth: 60,
            maxWidth: 80
        },
        {
            field: "timestampview",
            headerName: "Recently Updated",
            sort: "desc",
            minWidth: 170,
        },
    ];

    useEffect(() => {
        let temp = []
        if (
            !resultPostgres.loading &&
            resultPostgres.data &&
            resultPostgres.data.zkdata_max_ts &&
            resultPostgres.data.zkdata_max_ts.length > 0
        ) {
            let processedInstances = []
            resultPostgres.data.zkdata_max_ts.forEach((d) => {
                let instance = d.instance
                let process_id = d.process_id
                let uniqueId = instance + process_id

                if (!processedInstances.includes(uniqueId)) {
                    let ts = moment(d.timestamp);
                    let timestamp_temp = "";
                    timestamp_temp = moment(
                        moment(d.timestamp).format("YYYY-MM-DD HH:mm:ss.SSS") + " +0000"
                    )
                        .tz("UTC")
                        .format("YYYY-MM-DD HH:mm:ss.SSS ZZ");
                    timestamp_temp = moment(timestamp_temp)
                        .tz(token.timezone)
                        .format("YYYY-MM-DD HH:mm:ss.SSS ZZ");

                    let session_data = {
                        timestamp: ts,
                        timestampview: timestamp_temp,
                        instance: instance,
                        process_id: process_id
                    };
                    processedInstances.push(uniqueId)
                    temp.push(session_data)
                }
            });
            setLeftPanel(temp)
        }
    }, [resultPostgres.data, resultPostgres.loading, token.timezone]);

    useEffect(() => {
        if (
            !resultInstanceHistory.loading &&
            resultInstanceHistory.data &&
            resultInstanceHistory.data.zkdata &&
            resultInstanceHistory.data.zkdata.length > 0
        ) {
            let temp_tab_data = []

            Object.keys(resultInstanceHistory.data.zkdata).forEach(key => {

                const row = resultInstanceHistory.data.zkdata[key]

                let timestamp_temp = "";
                timestamp_temp = moment(
                    moment(row.timestamp).format("YYYY-MM-DD HH:mm:ss") + " +0000"
                )
                    .tz("UTC")
                    .format("YYYY-MM-DD HH:mm:ss ZZ");
                timestamp_temp = moment(timestamp_temp)
                    .tz(token.timezone)
                    .format("YYYY-MM-DD HH:mm:ss ZZ");
                temp_tab_data.push({
                    uniqueId: row.timestamp,
                    timestampview: timestamp_temp,
                    timestamp: row.timestamp
                });
            })
            setMiddlePanel(temp_tab_data)
        }
    }, [resultInstanceHistory.loading, resultInstanceHistory.data, token.timezone])

    const middlePanelColumns = [
        {
            field: "timestamp",
            headerName: "TimeStamp",
            hide: true,
        },
        {
            field: "mtime",
            headerName: "Epoch Time",
            hide: true,
        },
        {
            field: "timestampview",
            headerName: "TimeStamp",
            minWidth: 150,
        },
    ];

    return (
        <Grid
            rows={["90px", "700px"]}
            columns={["35%", "20%", "45%"]}
            gap="5px"
            areas={[
                ["title", "title", "title"],
                ["left", "middle", "detail"],
            ]}
        >
            <Box gridArea="title" background="light-3">
                <Box alignSelf="center" align="center">
                    <Text align="center" size="large" weight="bold">
                        {host} {service} {ocServiceName ? " / " + ocServiceName : ""} - Zookeeper Data History
                    </Text>
                    <Box
                        pad={{ left: "10px", right: "10px" }}
                        background="brand"
                        round="xsmall"
                        height="25px"
                        onClick={() => handleClickRefresh()}>
                        <Button plain label={<Text color="white" size="12px" weight="normal">Reload ZK History Data</Text>} margin={{ top: "xxsmall" }} hoverIndicator={{ color: "light-1", opacity: "weak" }} />
                    </Box>
                </Box>
            </Box>
            <Box
                gridArea="left"
                background="white"
            >
                {resultPostgres && resultPostgres.loading && <Spinner />}
                {!resultPostgres.loading && (
                    <AgGridReact
                        theme={themeBalham}
                        loadingCellRenderer={Spinner}
                        rowSelection="single"
                        enableCellTextSelection={true}
                        onRowClicked={(e) => {
                            handleClickNodeHistory(
                                e.api.getSelectedNodes()[0].data.instance,
                                e.api.getSelectedNodes()[0].data.process_id
                            );
                        }}
                        defaultColDef={{
                            sortable: true,
                            filter: true,
                            resizable: true,
                            cellStyle: {
                                "fontSize": "12px",
                                "fontFamily": "'Metrophobic', sans-serif",
                            },
                            floatingFilter: false,
                        }}
                        columnDefs={leftPanelColumns}
                        rowData={leftPanel}
                    />
                )}
            </Box>
            <Box
                gridArea="middle"
                background="white"
            >
                {resultInstanceHistory && resultInstanceHistory.loading && <Spinner />}
                {clickInstance &&
                    !resultInstanceHistory.loading &&
                    resultInstanceHistory &&
                    resultInstanceHistory.data &&
                    resultInstanceHistory.data.zkdata && (
                        <>
                            <Box direction="row" pad="small">
                                <Text size="small">Showing last {limit} entries</Text>
                                <Box
                                    pad={{ left: "10px", right: "10px" }}
                                    gap="none"
                                    margin={{ left: "10px" }}
                                    background="brand"
                                    round="xsmall"
                                    height="25px"
                                    onClick={() => setLimit(limit + DEFAULT_POSTGRES_LIMIT_INCREMENT)}
                                >
                                    <Button plain label={<Text color="white" size="12px" weight="normal">Load {limit} more</Text>} margin={{ top: "xxsmall" }} hoverIndicator={{ color: "light-1", opacity: "weak" }} />
                                </Box>
                            </Box>
                            <AgGridReact
                                theme={themeBalham}
                                loadingCellRenderer={Spinner}
                                rowSelection="single"
                                enableCellTextSelection={true}
                                onCellClicked={(e) => {
                                    handleClickTimestamp(
                                        e.api.getSelectedNodes()[0].data.timestamp
                                    );
                                }}
                                defaultColDef={{
                                    sortable: true,
                                    filter: true,
                                    resizable: true,
                                    cellStyle: {
                                        "fontSize": "12px",
                                        "fontFamily": "'Metrophobic', sans-serif",
                                    },
                                    floatingFilter: false,
                                }}
                                columnDefs={middlePanelColumns}
                                rowData={middlePanel}
                            />
                        </>
                    )}
            </Box>
            <Box gridArea="detail" background="white">
                {clickInstance && clickTimestamp &&
                    <ServiceHistoryDetailPostgres
                        instance={clickInstance}
                        timestamp={clickTimestamp}
                        host={host}
                        env={env}
                        service={service}
                    />
                }
            </Box>
        </Grid>
    );
};